import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  useReportSchedules,
  useUpdateReportSchedule,
} from "@/hooks/useAdminAnalytics";
import { AnalyticsPeriod } from "@/types/admin-analytics";
import { ReportScheduleRow } from "./ReportScheduleRow";

interface ReportScheduleCardProps {
  canEdit: boolean;
}

export const ReportScheduleCard: React.FC<ReportScheduleCardProps> = ({
  canEdit,
}) => {
  const { t } = useTranslation();
  const { data: schedules, isLoading, isError } = useReportSchedules();
  const updateSchedule = useUpdateReportSchedule();

  const handleSave = (
    period: AnalyticsPeriod,
    enabled: boolean,
    recipients: string[],
  ) => {
    if (!canEdit) return;
    updateSchedule.mutate(
      { period, enabled, recipients },
      {
        onSuccess: () => {
          toast.success(t("analytics.platform.reports.saved"));
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">
          {t("analytics.platform.reports.title")}
        </CardTitle>
        <CardDescription>
          {t("analytics.platform.reports.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isLoading && (
          <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
        )}
        {isError && (
          <p className="text-destructive text-sm">
            {t("analytics.platform.reports.error")}
          </p>
        )}
        {schedules?.map((config) => (
          <ReportScheduleRow
            key={config.period}
            config={config}
            onSave={(enabled, recipients) =>
              handleSave(config.period, enabled, recipients)
            }
            isSaving={
              updateSchedule.isPending &&
              updateSchedule.variables?.period === config.period
            }
            canEdit={canEdit}
          />
        ))}
      </CardContent>
    </Card>
  );
};
